/* eslint-disable complexity */
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { MonthParams } from './types';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';
import {
  getIsValidYear,
  getIsValidMonth,
  getStartDate,
} from '../../helpers/dateValidations';
import { ZieleWrapper } from './Ziele';
import { PlanungWrapper, TableWrapper } from './Planung';
import { useEffect, useMemo } from 'react';
import { toast } from '@/hooks/use-toast';
import { EinstellungenSheet } from './Einstellungen';
import { useProfile } from '@/hooks';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { YearMonthPicker } from '@/domain/controlling/components/YearMonthPicker';
import { Button } from '@e2n/ui';
import { getMonthNameByDate } from '@/utils';
import dayjs from 'dayjs';
import { MonthNavigationHeader } from '@/domain/controlling/pages/Umsatzplanung/components/MonthNavigationHeader';
import { EinstellungenSheetContextProvider } from '@/domain/controlling/pages/Umsatzplanung/Einstellungen/EinstellungenSheetContext/EinstellungenSheetContextProvider';
import { EinstellungenFormProvider } from '@/domain/controlling/pages/Umsatzplanung/Einstellungen/Forms/EinstellungenForm/EinstellungenFormProvider';

// eslint-disable-next-line max-lines-per-function
export function Page() {
  const { t } = useTranslation();
  const { year, month } = useParams<MonthParams>();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const [searchParams] = useSearchParams();
  const isValidYear = useMemo(() => getIsValidYear(year), [year]);
  const isValidMonth = useMemo(() => getIsValidMonth(month), [month]);
  const { manager } = useProfile();

  const startDate = useMemo(
    () => getStartDate(isValidYear, isValidMonth, year, month),
    [isValidMonth, isValidYear, month, year],
  );

  const { fallbackYear, fallbackMonth } = useMemo(() => {
    const currentDate = dayjs();
    return {
      fallbackYear: currentDate.get('year'),
      fallbackMonth: currentDate.get('month') + 1,
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('success')) {
      if (searchParams.get('type') === 'ziele') {
        toast({
          description: t('domain.controlling.umsatzplanung.ziele.success'),
        });
      } else {
        toast({
          description: t('domain.controlling.umsatzplanung.planung.success'),
        });
      }
    }
  }, [searchParams, t]);

  if (!isValidYear || !isValidMonth) {
    const defaultYear = new Date().getFullYear().toString();
    const defaultMonth = (new Date().getMonth() + 1).toString();
    return (
      <Navigate
        to={`/controlling/umsatzplanung/${defaultYear}/${defaultMonth}`}
        replace
      />
    );
  }

  function buildUrl(selectedDate: Date) {
    const year = dayjs(selectedDate).get('year');
    const month = dayjs(selectedDate).get('month') + 1;
    return `/controlling/umsatzplanung/${year}/${month}`;
  }

  return (
    <>
      <Helmet>
        <title>{t('domain.controlling.umsatzplanung.header')}</title>
      </Helmet>
      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center">
          <MonthNavigationHeader startDate={startDate} />
          <YearMonthPicker
            buildUrl={buildUrl}
            selectedDate={new Date(startDate)}>
            <Button variant="ghost" className="text-2xl font-extrabold">
              {getMonthNameByDate(dayjs(startDate).format('YYYY-MM-DD'))}
            </Button>
          </YearMonthPicker>
        </div>
        {manager?.hasEinstellungenRecht() && (
          <EinstellungenSheetContextProvider>
            <EinstellungenFormProvider>
              <EinstellungenSheet />
            </EinstellungenFormProvider>
          </EinstellungenSheetContextProvider>
        )}
      </div>
      <ZieleWrapper />
      <PlanungWrapper />
      <Outlet />
      <TableWrapper
        umsatzplanungAnzeige={umsatzplanungAnzeige ?? 'UMSATZ_PRODUKTIVITAET'}
        year={parseInt(year ?? fallbackYear.toString())}
        month={parseInt(month ?? fallbackMonth.toString())}
      />
    </>
  );
}
